import { useEffect, useState } from 'react';
import { Autocomplete, Box, Checkbox, TextField } from '@mui/material';
import { useTranslation } from '../../../i18n/useTranslation';

interface OrderingDropdownFilterProps {
    values?: string[];
    name: string;
    featureId: string;
    onEnumValuesChange: (featureId: string, enumValues: string[] | boolean) => void;
    isBoolean: boolean;
    clear?: number;
}

const checkboxSx = {
    p: 0.5,
    '&.Mui-checked': { color: '#821818' },
};

const controlBoxSx = {
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    height: 40,
    display: 'flex',
    alignItems: 'center',
};

const SELECT_ALL_OPTION = '__SELECT_ALL__';

// MUI checkbox-multiselect used by OrderingTableFilters. ProductFiltersMenu uses its own DropdownList
// component and should stay that way - keep the two separate.
const OrderingDropdownFilter = ({
    values,
    featureId,
    onEnumValuesChange,
    isBoolean,
    clear,
}: OrderingDropdownFilterProps) => {
    const { t } = useTranslation('translation');
    const allValues = values ?? [];
    const [enumValues, setEnumValues] = useState<string[]>(allValues);
    const [booleanValue, setBooleanValue] = useState<boolean>(false);

    // All values are selected by default, matching "no filter applied"; narrows only once the user
    // manually unchecks something.
    useEffect(() => {
        if (!isBoolean) {
            setEnumValues(allValues);
            onEnumValuesChange(featureId, allValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    useEffect(() => {
        setEnumValues(allValues);
        setBooleanValue(false);
        onEnumValuesChange(featureId, isBoolean ? false : allValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clear]);

    if (isBoolean) {
        return (
            <Box sx={{ ...controlBoxSx, justifyContent: 'center' }}>
                <Checkbox
                    sx={checkboxSx}
                    checked={booleanValue}
                    onChange={(event) => {
                        setBooleanValue(event.target.checked);
                        onEnumValuesChange(featureId, event.target.checked);
                    }}
                />
            </Box>
        );
    }

    const isAllSelected = allValues.length > 0 && enumValues.length === allValues.length;

    const handleChange = (selected: string[]) => {
        if (selected.includes(SELECT_ALL_OPTION)) {
            const next = isAllSelected ? [] : allValues;
            setEnumValues(next);
            onEnumValuesChange(featureId, next);
            return;
        }
        setEnumValues(selected);
        onEnumValuesChange(featureId, selected);
    };

    return (
        <Autocomplete
            multiple
            size="small"
            options={allValues.length > 0 ? [SELECT_ALL_OPTION, ...allValues] : allValues}
            value={enumValues}
            disableCloseOnSelect
            onChange={(_event, selected) => handleChange(selected)}
            getOptionLabel={(option) => (option === SELECT_ALL_OPTION ? t('SELECT_ALL') : option)}
            renderOption={(props, option) => (
                <Box component="li" {...props}>
                    <Checkbox
                        sx={checkboxSx}
                        checked={option === SELECT_ALL_OPTION ? isAllSelected : enumValues.includes(option)}
                    />
                    {option === SELECT_ALL_OPTION ? t('SELECT_ALL') : option}
                </Box>
            )}
            renderInput={(params) => <TextField {...params} />}
        />
    );
};

export default OrderingDropdownFilter;
